import { CheckCircle2, ShieldCheck, Clock } from "lucide-react";

export default function Hero({ onCtaClick }) {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-10 pb-20 sm:pt-12 sm:pb-24 lg:pt-20 lg:pb-32 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
        <div className="tr-fade-up">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 bg-[#F1EDE7] text-[#2C3D30] px-4 py-2 rounded-full text-[11px] sm:text-xs font-medium tracking-wide max-w-full"
          >
            <span className="w-2 h-2 rounded-full bg-[#D95D39] shrink-0" />
            <span className="truncate">Family-run · PA · NJ · AL · TX · NC · SC · GA</span>
          </span>

          <h1
            data-testid="hero-title"
            className="mt-6 font-[Outfit] font-semibold text-[#2C3D30] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Sell your home
            <br />
            <span className="text-[#D95D39]">hassle free.</span>
            <br />
            For cash.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-base lg:text-lg text-[#5B6B5F] leading-relaxed max-w-xl"
          >
            We&apos;re cash buyers for single-family homes. We buy in as-is condition and can close
            in <strong className="text-[#2C3D30]">3 weeks or less</strong>. No repairs. No fees. No agents.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-cta-button"
              onClick={onCtaClick}
              className="tr-btn-primary rounded-full px-8 h-14 font-medium text-base inline-flex items-center justify-center"
            >
              Get My Cash Offer →
            </button>
            <a
              href="#how-it-works"
              data-testid="hero-secondary-button"
              className="tr-btn-secondary rounded-full px-8 h-14 font-medium text-base inline-flex items-center justify-center"
            >
              How It Works
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <Stat icon={<CheckCircle2 strokeWidth={1.5} />} title="As-Is" subtitle="No repairs needed" testid="stat-asis" />
            <Stat icon={<ShieldCheck strokeWidth={1.5} />} title="No Fees" subtitle="Zero commissions" testid="stat-nofees" />
            <Stat icon={<Clock strokeWidth={1.5} />} title="3 Weeks" subtitle="Or less to close" testid="stat-3weeks" />
          </div>
        </div>

        <div className="relative tr-fade-up" style={{ animationDelay: "150ms" }}>
          <div className="absolute -top-6 -left-6 w-44 h-44 bg-[#F1EDE7] rounded-3xl -z-10" />
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#D95D39]/15 rounded-3xl -z-10" />
          <div className="rounded-3xl overflow-hidden border border-[#E2DDD7] shadow-[0_30px_60px_-20px_rgba(44,61,48,0.25)]">
            <img
              data-testid="hero-image"
              src="https://images.unsplash.com/photo-1751050743813-03d46859896c"
              alt="A charming family home"
              className="w-full h-[520px] object-cover"
              loading="eager"
            />
          </div>

          <div
            data-testid="hero-floating-card"
            className="absolute -bottom-8 left-6 right-6 sm:left-10 sm:right-auto bg-white rounded-2xl border border-[#E2DDD7] p-5 shadow-[0_20px_50px_-15px_rgba(44,61,48,0.25)]"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F1EDE7] flex items-center justify-center text-[#D95D39] font-[Outfit] font-semibold text-lg">
                $
              </div>
              <div>
                <div className="text-xs text-[#5B6B5F] uppercase tracking-wider">Fair Cash Offer</div>
                <div className="font-[Outfit] font-semibold text-[#2C3D30] text-lg">Within 24 hours</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, title, subtitle, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col gap-1.5">
      <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DDD7] flex items-center justify-center text-[#D95D39]">
        {icon}
      </div>
      <div className="font-[Outfit] font-semibold text-[#2C3D30] text-base mt-1">{title}</div>
      <div className="text-xs text-[#5B6B5F]">{subtitle}</div>
    </div>
  );
}
